import React, { useState } from 'react';
import { Menu, Wifi, WifiOff, Bell, User, Sun, Moon } from 'lucide-react';
import { formatDateTimeToHHMMSS } from '../utils/timeFormatter';
import { useSocket } from '../contexts/SocketContext';
import { useTheme } from '../contexts/ThemeContext';
import TenantSelector from './TenantSelector';
import logger from '../utils/logger';

const Header = ({ user, socketConnected, onToggleSidebar, sidebarCollapsed, isMobile }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const { alarms } = useSocket();
  const { theme, toggle } = useTheme();

  const unreadAlarms = alarms.filter(alarm => !alarm.acknowledged).length;

  // Debug: Log при секој рендер
  logger.log('Header render:', { 
    sidebarCollapsed, 
    isMobile, 
    onToggleSidebar: typeof onToggleSidebar 
  });

  const handleToggleSidebar = (e) => {
    logger.log('Header: Toggle sidebar clicked', { 
      sidebarCollapsed, 
      isMobile,
      event: e,
      target: e.target,
      currentTarget: e.currentTarget
    });
    e.preventDefault();
    e.stopPropagation();
    onToggleSidebar();
  };

  return (
    <div className="content-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.75rem' : '1rem' }}>
        <button
          onClick={handleToggleSidebar}
          className="btn btn-secondary btn-sm hamburger-btn"
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '0.5rem',
            minWidth: '44px',
            minHeight: '44px',
            zIndex: 10000,
            position: 'relative',
            cursor: 'pointer',
            touchAction: 'manipulation',
            flexShrink: 0
          }}
          aria-label="Toggle menu"
          type="button"
        >
          <Menu size={20} />
        </button>
        
        <h1 style={{ 
          margin: 0, 
          fontSize: isMobile ? '1.1rem' : '1.5rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flex: 1,
          minWidth: 0
        }}>
          Ovatik Fleet
        </h1>
      </div>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '0.5rem',
        flexWrap: 'wrap'
      }}>
        {/* Tenant Selector */}
        <TenantSelector isMobile={isMobile} />
        
        {/* Connection Status - унифициран стил како копче */}
        <button
          className="btn btn-sm header-info-btn"
          style={{ 
          display: 'flex', 
          alignItems: 'center', 
            gap: '0.5rem',
            padding: '0.5rem 0.75rem',
          backgroundColor: socketConnected ? '#d4edda' : '#f8d7da',
          color: socketConnected ? '#155724' : '#721c24',
            border: 'none',
            borderRadius: '4px',
            fontSize: '0.85rem',
            cursor: 'default',
            minWidth: '44px',
            minHeight: '44px',
            justifyContent: 'center'
          }}
          disabled
          aria-label={socketConnected ? 'Поврзан' : 'Неповрзан'}
        >
          {socketConnected ? <Wifi size={16} /> : <WifiOff size={16} />}
          {!isMobile && (
            <span>{socketConnected ? 'Поврзан' : 'Неповрзан'}</span>
          )}
        </button>
        
        {/* Theme toggle (light/dark) */}
        <button
          onClick={toggle}
          className="btn btn-secondary btn-sm"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '44px', minHeight: '44px', padding: '0.5rem' }}
          aria-label={theme === 'dark' ? 'Светла тема' : 'Темна тема'}
          title={theme === 'dark' ? 'Светла тема' : 'Темна тема'}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Notifications - унифициран стил */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="btn btn-secondary btn-sm"
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '0.5rem 0.75rem',
              position: 'relative',
              minWidth: '44px',
              minHeight: '44px',
              gap: '0.5rem'
            }}
            aria-label="Notifications"
          >
            <Bell size={18} />
            {!isMobile && unreadAlarms > 0 && (
              <span style={{ fontSize: '0.85rem' }}>
                {unreadAlarms}
              </span>
            )}
            {unreadAlarms > 0 && (
              <span style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                backgroundColor: '#dc3545',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '0.7rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                border: '2px solid white'
              }}>
                {unreadAlarms > 9 ? '9+' : unreadAlarms}
              </span>
            )}
          </button>
          
          {showNotifications && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '0.5rem',
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              minWidth: window.innerWidth > 480 ? '300px' : '250px',
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1000
            }}>
              <div style={{ 
                padding: '1rem', 
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600'
              }}>
                Аларми ({unreadAlarms} непрочитани)
              </div>
              
              {alarms.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Нема аларми
                </div>
              ) : (
                <div>
                  {alarms.slice(0, 10).map((alarm, index) => (
                    <div key={index} style={{
                      padding: '0.75rem 1rem',
                      borderBottom: '1px solid #f8f9fa',
                      fontSize: '0.9rem'
                    }}>
                      <div style={{ 
                        fontWeight: '500',
                        color: alarm.severity === 'critical' ? '#dc3545' : 
                               alarm.severity === 'high' ? '#fd7e14' : '#6c757d'
                      }}>
                        {alarm.alarmType}
                      </div>
                      <div style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                        {alarm.message}
                      </div>
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: 'var(--text-muted)',
                        marginTop: '0.25rem'
                      }}>
                        {formatDateTimeToHHMMSS(alarm.timestamp)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* User Info - унифициран стил како копче */}
        <button
          className="btn btn-secondary btn-sm header-info-btn"
          style={{ 
          display: 'flex', 
          alignItems: 'center', 
            gap: '0.5rem',
            padding: '0.5rem 0.75rem',
            fontSize: '0.85rem',
            minWidth: '44px',
            minHeight: '44px',
            justifyContent: 'center'
          }}
          disabled
          aria-label={`User: ${user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.username || 'User'}`}
        >
          <User size={16} />
          {!isMobile && (
            <span>
              {user?.first_name && user?.last_name 
                ? `${user.first_name} ${user.last_name}`
                : user?.username || 'User'}
          </span>
          )}
          <span style={{ 
            fontSize: '0.75rem', 
            color: 'var(--text-muted)',
            textTransform: 'capitalize'
          }}>
            ({user?.role})
          </span>
        </button>
      </div>
    </div>
  );
};

export default Header;













